import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import { X } from 'lucide-react';
import { Send } from 'lucide-react';
import { Avatar, AvatarGroup } from '@heroui/react';
const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
`;
const ModalContainer = styled.div`
  background-color: var(--theme-paper, white);
  border-radius: 1rem;
  width: 90%;
  max-width: 500px;
  height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .dark & {
    background-color: var(--theme-paper, #1f2937);
  }
`;
const Header = styled.header`
    display: flex;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--theme-border, #e5e7eb);

    .dark & {
        border-color: var(--theme-border, #374151);
    }
`;
const HeaderInfo = styled.div`
    margin-left: 0.75rem;
    font-weight: 600;
`;
const CloseButton = styled.button`
    margin-left: auto;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--theme-text-muted, #9ca3af);
`;
const MessageContainer = styled.div`
    flex-grow: 1;
    padding: 1.5rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column-reverse;
`;
const Footer = styled.footer`
    padding: 1rem 1.5rem;
    border-top: 1px solid var(--theme-border, #e5e7eb);
    background-color: var(--theme-paper-hover, #f9fafb);

    .dark & {
        border-color: var(--theme-border, #374151);
        background-color: var(--theme-paper-hover, #374151);
    }
`;
const InputWrapper = styled.div`
    display: flex;
    align-items: center;
    background-color: var(--theme-paper, white);
    border-radius: 9999px;
    padding: 0.25rem 0.5rem 0.25rem 1rem;
    border: 1px solid var(--theme-border-dark, #d1d5db);

    .dark & {
      background-color: var(--theme-paper, #1f2937);
      border-color: var(--theme-border-dark, #4b5563);
    }
`;
const ChatInput = styled.input`
    width: 100%;
    border: none;
    background: transparent;
    font-size: 1rem;
    color: var(--theme-text, #111827);

    &:focus {
        outline: none;
    }
    
    .dark & {
        color: var(--theme-text, #f3f4f6);
    }
`;
const SendButton = styled.button`
    background-color: var(--theme-primary, #10b981);
    color: white;
    border: none;
    border-radius: 50%;
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover:not(:disabled) {
        background-color: var(--theme-primary-dark, #059669);
    }

    &:disabled {
        background-color: var(--theme-text-muted, #9ca3af);
        cursor: not-allowed;
        opacity: 0.5;
    }
`;
const MessageBubble = styled.div`
    max-width: 75%;
    padding: 0.75rem 1rem;
    border-radius: 1.25rem;
    margin-bottom: 0.75rem;
    align-self: ${(props)=>props.isSender ? 'flex-end' : 'flex-start'};
    background-color: ${(props)=>props.isSender ? 'var(--theme-primary, #10b981)' : 'var(--theme-paper-active, #f3f4f6)'};
    color: ${(props)=>props.isSender ? 'white' : 'var(--theme-text, #111827)'};

    .dark & {
      background-color: ${(props)=>props.isSender ? 'var(--theme-primary, #10b981)' : 'var(--theme-paper-active, #374151)'};
      color: ${(props)=>props.isSender ? 'white' : 'var(--theme-text, #f3f4f6)'};
    }
`;
const UserChatModal = ({ isOpen, onClose, chatRoom, userId, token })=>{
    const [newMessage, setNewMessage] = useState('');
    const messageEndRef = useRef(null);
    // RSocket 챗 훅 사용
    const { isConnected, error, sendMessage, joinRoom, leaveRoom, getChatRoom } = useRSocketChat({
        userId,
        token
    });
    // 실시간 챗룸 데이터 가져오기
    const realTimeChatRoom = chatRoom ? getChatRoom(chatRoom.id) : null;
    const currentChatRoom = realTimeChatRoom || chatRoom;
    useEffect(()=>{
        messageEndRef.current?.scrollIntoView();
    }, [
        currentChatRoom?.messages
    ]);
    // 챗룸 참여/퇴장 관리
    useEffect(()=>{
        if (isOpen && chatRoom && isConnected) {
            joinRoom(chatRoom.id);
            return ()=>{
                leaveRoom(chatRoom.id);
            };
        }
    }, [
        isOpen,
        chatRoom,
        isConnected,
        joinRoom,
        leaveRoom
    ]);
    if (!isOpen || !currentChatRoom) return null;
    const handleSendMessage = async ()=>{
        if (newMessage.trim() === '' || !currentChatRoom) return;
        try {
            await sendMessage(currentChatRoom.id, newMessage.trim());
            setNewMessage('');
        } catch (error) {
            console.error('Failed to send message:', error);
        // 에러 처리 (토스트 알림 등)
        }
    };
    const otherParticipants = currentChatRoom.participants.filter((p)=>p.id !== userId);
    // 연결 상태 표시
    if (!isConnected && error) {
        return /*#__PURE__*/ _jsx(ModalBackdrop, {
            onClick: onClose,
            children: /*#__PURE__*/ _jsxs(ModalContainer, {
                onClick: (e)=>e.stopPropagation(),
                children: [
                    /*#__PURE__*/ _jsxs(Header, {
                        children: [
                            /*#__PURE__*/ _jsx(HeaderInfo, {
                                children: "연결 오류"
                            }),
                            /*#__PURE__*/ _jsx(CloseButton, {
                                onClick: onClose,
                                children: /*#__PURE__*/ _jsx(X, {
                                    size: 24
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(MessageContainer, {
                        children: /*#__PURE__*/ _jsxs("div", {
                            style: {
                                textAlign: 'center',
                                padding: '2rem'
                            },
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    children: "채팅 서버에 연결할 수 없습니다."
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    children: error
                                })
                            ]
                        })
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsx(ModalBackdrop, {
        onClick: onClose,
        children: /*#__PURE__*/ _jsxs(ModalContainer, {
            onClick: (e)=>e.stopPropagation(),
            children: [
                /*#__PURE__*/ _jsxs(Header, {
                    children: [
                        currentChatRoom.isGroupThread ? /*#__PURE__*/ _jsx(AvatarGroup, {
                            children: otherParticipants.slice(0, 3).map((p)=>/*#__PURE__*/ _jsx(Avatar, {
                                    src: p.avatarUrl,
                                    alt: p.displayName
                                }, p.id))
                        }) : /*#__PURE__*/ _jsx(Avatar, {
                            src: otherParticipants[0]?.avatarUrl,
                            alt: otherParticipants[0]?.displayName
                        }),
                        /*#__PURE__*/ _jsxs(HeaderInfo, {
                            children: [
                                currentChatRoom.isGroupThread ? currentChatRoom.participants.map((p)=>p.displayName).join(', ') : otherParticipants[0]?.displayName,
                                !isConnected && /*#__PURE__*/ _jsx("span", {
                                    style: {
                                        color: '#fbbf24',
                                        marginLeft: '8px'
                                    },
                                    children: "• 연결 중..."
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(CloseButton, {
                            onClick: onClose,
                            children: /*#__PURE__*/ _jsx(X, {
                                size: 24
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(MessageContainer, {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            ref: messageEndRef
                        }),
                        currentChatRoom.messages.slice().reverse().map((msg)=>/*#__PURE__*/ _jsx(MessageBubble, {
                                isSender: msg.sender.id === userId,
                                children: msg.text
                            }, msg.id)),
                        currentChatRoom.messages.length === 0 && /*#__PURE__*/ _jsx("div", {
                            style: {
                                textAlign: 'center',
                                padding: '2rem',
                                color: '#9ca3af'
                            },
                            children: "아직 메시지가 없습니다."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Footer, {
                    children: /*#__PURE__*/ _jsxs(InputWrapper, {
                        children: [
                            /*#__PURE__*/ _jsx(ChatInput, {
                                placeholder: "메시지를 입력하세요...",
                                value: newMessage,
                                onChange: (e)=>setNewMessage(e.target.value),
                                onKeyDown: (e)=>e.key === 'Enter' && handleSendMessage()
                            }),
                            /*#__PURE__*/ _jsx(SendButton, {
                                onClick: handleSendMessage,
                                disabled: !isConnected,
                                children: /*#__PURE__*/ _jsx(Send, {
                                    size: 20
                                })
                            })
                        ]
                    })
                })
            ]
        })
    });
};
export default UserChatModal;
